/**
 * Partial-lead payloads for POST /apply/lead.
 *
 * Pure and network-free so it can be unit-tested directly — the event
 * wiring that calls it cannot be, since @testing-library is not a
 * dependency and these fire from browser lifecycle events.
 *
 * The contract with the server (2026-08-13 spec, §3) is that an absent key
 * means "no answer yet" and must leave any stored value untouched. So an
 * empty field is OMITTED, never sent as ''. Sending '' would blank a value
 * captured by an earlier, richer write.
 */

/** Everything a lead may carry. Deliberately an allow-list, not a
 *  blacklist: `form` also holds contact preferences and address fields
 *  that the lead has no use for, and a future field added to the wizard
 *  should not start flowing to a public endpoint by accident. */
export const LEAD_FIELDS = [
  'caseType',
  'accidentState',
  'requestedAmount',
  'firstName',
  'lastName',
  'phone',
  'attorneyName',
  'attorneyEmail',
  'lawFirmName',
  'firm_id',
];

/** Same shape the wizard's own validate() uses, so the client and the
 *  endpoint agree on what counts as an addressable lead. */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const isLeadEmailValid = (email) => EMAIL_RE.test((email || '').trim());

export const buildLeadPayload = (form, step) => {
  const payload = {
    email: (form.email || '').trim().toLowerCase(),
    furthest_step: step,
  };

  LEAD_FIELDS.forEach((key) => {
    let value = form[key];
    if (value === undefined || value === null) return;

    if (key === 'requestedAmount') {
      // Held as a display-formatted string ("5,000") by formatCurrencyInput.
      const amount = parseFloat(String(value).replace(/[^0-9.]/g, ''));
      if (!amount || Number.isNaN(amount)) return;
      payload[key] = amount;
      return;
    }

    if (typeof value === 'string') {
      value = value.trim();
      if (!value) return;
    }

    payload[key] = value;
  });

  return payload;
};
